import { useCallback, useEffect, useState } from "react";
import { authFetch } from "../utils/authFetch";

interface Hook { id: string; url: string; lastStatus: number | null; lastError: string | null; lastDeliveredAt: string | null }

/** Tell other systems when this project publishes. Super admins only; the server enforces it. */
export default function WebhooksPanel({ projectId }: { projectId: string }) {
  const [hooks, setHooks] = useState<Hook[]>([]);
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [error, setError] = useState("");
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    const r = await authFetch(`/api/admin/projects/${projectId}/webhooks`);
    if (r.status === 403) { setDenied(true); return; }
    if (r.ok) setHooks((await r.json()).webhooks);
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  if (denied) return null;

  const add = async () => {
    setError(""); setSecret("");
    const r = await authFetch(`/api/admin/projects/${projectId}/webhooks`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ url: url.trim() }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setError(d.detail || "Couldn't add that webhook."); return; }
    setSecret(d.secret); setUrl(""); load();
  };
  const remove = async (id: string) => {
    await authFetch(`/api/admin/projects/${projectId}/webhooks/${id}`, { method: "DELETE" });
    load();
  };

  return (
    <section className="mt-8 pt-6 border-t border-slate-100" aria-labelledby="webhooks-title">
      <h3 id="webhooks-title" className="text-[13px] font-semibold text-slate-800">Publish webhooks</h3>
      <p className="text-[12px] text-slate-500 mt-1 mb-3">Cortex sends a signed POST to these URLs each time this project publishes.</p>
      <ul className="text-[12px] space-y-1.5 mb-3">
        {hooks.map((h) => (
          <li key={h.id} className="flex items-center justify-between gap-3">
            <span className="font-mono break-all">{h.url}</span>
            <span className="shrink-0 text-slate-500">{h.lastDeliveredAt ? (h.lastError ? `Failed: ${h.lastError}` : `Delivered (${h.lastStatus})`) : "Not sent yet"}</span>
            <button type="button" onClick={() => remove(h.id)} className="shrink-0 text-slate-500 hover:text-red-600">Remove</button>
          </li>
        ))}
      </ul>
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); add(); }}>
        <input aria-label="Webhook URL" type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/hook"
          className="flex-1 px-2.5 py-1.5 text-[12px] font-mono border border-slate-200 rounded-lg" />
        <button type="submit" disabled={!url.trim()} className="px-3 py-1.5 text-[12px] font-medium rounded-lg bg-slate-800 text-white disabled:opacity-40">Add webhook</button>
      </form>
      {error && <p role="alert" className="text-[12px] text-red-600 mt-2">{error}</p>}
      {secret && <p className="text-[12px] text-slate-700 mt-2">Signing secret, shown once: <code className="break-all">{secret}</code></p>}
    </section>
  );
}
